// frontend/src/pages/PayrollDone/index.tsx
// Thin orchestrator: 3 data hooks, derived values, layout.
// Delegates heavy JSX to NextSteps, EmployeeTable, PageFooter sub-components.

import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check } from "lucide-react";

import {
  fmtMoney,
  fmtDate,
  fmtDateFull,
  scheduleFrequencyLabel,
  downloadPayRunCsv,
} from "./helpers";
import { C, FONT, tabular, outlineBtn, sectionLabel } from "./styles";

import { usePayRunDoneView } from "./usePayRunDoneView";
import { usePayrollSettings } from "./usePayrollSettings";
import { usePaySchedule } from "./usePaySchedule";

import LoadingState from "../../components/common/LoadingState";

import NextSteps from "./components/NextSteps";
import EmployeeTable from "./components/EmployeeTable";
import PageFooter from "./components/PageFooter";

export default function PayrollDone(): React.ReactElement {
  const { payRunId } = useParams();
  const navigate = useNavigate();

  // Local state: per-row cheque-number inputs (session-only, not persisted — matches original)
  const [chequeNumbers, setChequeNumbers] = useState<Record<string, string>>({});

  // ── Data ────────────────────────────────────────────────────────────────
  const {
    data: doneView,
    isLoading: loadingDone,
    error: doneError,
  } = usePayRunDoneView(payRunId);
  const { data: payrollSettings } = usePayrollSettings();
  const { data: schedule } = usePaySchedule(doneView?.run?.pay_schedule_id);

  // ── Loading / error early returns ───────────────────────────────────────
  if (loadingDone) {
    return (
      <div style={{ padding: "40px 32px", background: C.page, minHeight: "100vh", fontFamily: FONT }}>
        <LoadingState message="Loading pay run..." padding={0} color={C.ink} fontSize={15} />
      </div>
    );
  }

  if (doneError || !doneView || !doneView.totals || !doneView.run) {
    return (
      <div style={{ padding: "40px 32px", background: C.page, minHeight: "100vh", fontFamily: FONT }}>
        <div style={{ fontSize: 18, color: C.ink, fontWeight: 700, marginBottom: 8 }}>
          Could not load this pay run.
        </div>
        <div style={{ fontSize: 14, color: C.ink, fontWeight: 500, marginBottom: 20 }}>
          {(doneError as Error)?.message || "Unknown error"}
        </div>
        <button
          onClick={() => navigate("/payroll/pay-runs")}
          style={{ ...outlineBtn, padding: "10px 16px" }}
        >
          Back to Pay Runs
        </button>
      </div>
    );
  }

  // ── Derived values ──────────────────────────────────────────────────────
  const { run, employees, totals } = doneView;

  const chequeCount = totals.cheque_count || 0;
  const empsPaid = totals.employees_paid || 0;
  const empWord = empsPaid === 1 ? "employee" : "employees";
  const takeHome = Number(totals.employee_take_home || 0);
  const totalCost = Number(totals.total_cost || 0);
  const employeeTax = Number(totals.employee_tax || 0);
  const employerTax = Number(totals.employer_tax || 0);
  const craTotal = employeeTax + employerTax;

  const postingAccount = (payrollSettings && payrollSettings.company_bank_name) || "Not set";
  const scheduleFreq = schedule ? scheduleFrequencyLabel(schedule.frequency) : "";
  const scheduleName = schedule ? (schedule.name || scheduleFreq) : "";

  // ── Handlers ────────────────────────────────────────────────────────────
  function updateChequeNumber(stubId: string, num: string): void {
    setChequeNumbers((prev) => ({ ...prev, [stubId]: num }));
  }

  function openPayStub(stubId: string): void {
    window.open("/payroll/paycheques/" + stubId, "_blank");
  }

  function printAllPayStubs(): void {
    employees.forEach(function (emp, idx) {
      setTimeout(function () {
        openPayStub(emp.stub_id);
      }, idx * 200);
    });
  }

  async function handleExportCsv(): Promise<void> {
    try {
      await downloadPayRunCsv(payRunId!, run);
    } catch (e) {
      alert("Export failed: " + ((e as Error).message || e));
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        maxWidth: "100%",
        margin: 0,
        padding: "28px 32px 110px",
        fontFamily: FONT,
        background: C.page,
        minHeight: "100vh",
      }}
    >
      <div style={{ background: C.card, borderRadius: 12, padding: "40px 48px" }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: C.ink, marginBottom: 14 }}>
          <a
            onClick={() => navigate("/payroll/overview")}
            style={{ color: C.ink, fontWeight: 600, textDecoration: "none", opacity: 0.7, cursor: "pointer" }}
          >
            Payroll
          </a>
          <span style={{ color: C.ink, opacity: 0.4 }}>/</span>
          <a
            onClick={() => navigate("/payroll/overview")}
            style={{ color: C.ink, fontWeight: 600, textDecoration: "none", opacity: 0.7, cursor: "pointer" }}
          >
            Pay runs
          </a>
          <span style={{ color: C.ink, opacity: 0.4 }}>/</span>
          <span style={{ color: C.ink, fontWeight: 700 }}>Payroll complete</span>
        </div>

        {/* Title + success + FINALIZED badge */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 40 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: C.brand,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Check size={22} strokeWidth={3} stroke={C.card} />
              </div>
              <div style={{ fontSize: 34, fontWeight: 700, color: C.ink, letterSpacing: "-0.02em", lineHeight: 1 }}>
                Payroll complete
              </div>
            </div>
            <div style={{ fontSize: 14, color: C.ink, fontWeight: 500 }}>
              You paid <strong>{empsPaid} {empWord}</strong> for the pay period ending{" "}
              {fmtDate(run.pay_period_end)}.
            </div>
          </div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              background: C.brandBg,
              borderRadius: 6,
              fontSize: 12,
              color: C.brandDark,
              fontWeight: 700,
            }}
          >
            <span style={{ width: 6, height: 6, background: C.brand, borderRadius: "50%" }} />
            FINALIZED
          </div>
        </div>

        {/* Meta grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 32,
            paddingBottom: 32,
            borderBottom: "1px solid " + C.line,
            marginBottom: 40,
          }}
        >
          <div>
            <div style={sectionLabel}>Pay schedule</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{scheduleName || "Not set"}</div>
            {scheduleFreq && scheduleName !== scheduleFreq && (
              <div style={{ fontSize: 12, color: C.ink, fontWeight: 500, marginTop: 2 }}>{scheduleFreq}</div>
            )}
          </div>
          <div>
            <div style={sectionLabel}>Pay period</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, ...tabular }}>
              {fmtDate(run.pay_period_start)} to {fmtDate(run.pay_period_end)}
            </div>
          </div>
          <div>
            <div style={sectionLabel}>Pay date</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, ...tabular }}>
              {fmtDateFull(run.pay_date)}
            </div>
          </div>
          <div>
            <div style={sectionLabel}>Posting account</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{postingAccount}</div>
          </div>
        </div>

        {/* Big totals: Total cost + Employees paid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.6fr auto 1fr",
            gap: 40,
            marginBottom: 40,
            alignItems: "flex-start",
          }}
        >
          <div>
            <div style={{ ...sectionLabel, marginBottom: 14 }}>Total payroll cost</div>
            <div
              style={{
                fontSize: 56,
                fontWeight: 700,
                color: C.ink,
                letterSpacing: "-0.03em",
                lineHeight: 1,
                ...tabular,
              }}
            >
              {fmtMoney(totalCost)}
            </div>
          </div>
          <div style={{ width: 1, height: 96, background: C.line, marginTop: 20 }} />
          <div>
            <div style={{ ...sectionLabel, marginBottom: 14 }}>Employees paid</div>
            <div
              style={{
                fontSize: 56,
                fontWeight: 700,
                color: C.ink,
                letterSpacing: "-0.03em",
                lineHeight: 1,
                ...tabular,
              }}
            >
              {empsPaid}
            </div>
          </div>
        </div>

        {/* Split: Paid to employees + Owed to CRA */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            gap: 40,
            paddingTop: 28,
            paddingBottom: 40,
            borderTop: "1px solid " + C.line,
            borderBottom: "1px solid " + C.line,
            marginBottom: 40,
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ ...sectionLabel, letterSpacing: "0.1em", opacity: 0.7, marginBottom: 8 }}>
              Paid to employees
            </div>
            <div
              style={{
                fontSize: 32,
                fontWeight: 700,
                color: C.ink,
                letterSpacing: "-0.02em",
                lineHeight: 1,
                ...tabular,
                marginBottom: 6,
              }}
            >
              {fmtMoney(takeHome)}
            </div>
            <div style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>Net pay after deductions</div>
          </div>
          <div style={{ width: 1, height: 90, background: C.line }} />
          <div>
            <div style={{ ...sectionLabel, letterSpacing: "0.1em", opacity: 0.7, marginBottom: 8 }}>
              Owed to CRA
            </div>
            <div
              style={{
                fontSize: 32,
                fontWeight: 700,
                color: C.ink,
                letterSpacing: "-0.02em",
                lineHeight: 1,
                ...tabular,
                marginBottom: 6,
              }}
            >
              {fmtMoney(craTotal)}
            </div>
            <div style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>
              Employee {fmtMoney(employeeTax)} + Employer {fmtMoney(employerTax)}
            </div>
          </div>
        </div>

        {/* What's next */}
        <NextSteps
          chequeCount={chequeCount}
          empsPaid={empsPaid}
          craTotal={craTotal}
          employeeTax={employeeTax}
          employerTax={employerTax}
          payDate={run.pay_date}
          employees={employees}
          onViewLiabilities={() => navigate("/payroll/compliance")}
        />

        {/* Paid this run */}
        <EmployeeTable
          employees={employees}
          chequeNumbers={chequeNumbers}
          onChequeNumberChange={updateChequeNumber}
          onOpenPayStub={openPayStub}
        />
      </div>

      {/* Fixed footer */}
      <PageFooter
        onBack={() => navigate("/payroll/pay-runs")}
        onExportCsv={handleExportCsv}
        onPrintAll={printAllPayStubs}
      />
    </div>
  );
}